'use client'

import { useCallback, useState } from 'react'

import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA, NOT_SET } from '@/app/lib/analytics/constants'

import styles from './ExpandableDescription.module.scss'

type ExpandableDescriptionProps = {
  readonly analyticsId?: string
} & (
  | { readonly text: string; readonly html?: never }
  /** HTML YA saneado en servidor (p. ej. `resolvePageContentBody`) — este
   * componente es de cliente y no sanea. */
  | { readonly html: string; readonly text?: never }
)

const ANALYTICS_LABEL = 'descripcion'

/**
 * Descripción colapsada a 3 líneas con alternador "Ver más"/"Ver menos",
 * igual que la descripción de `PageIntro` en el proyecto de referencia. El
 * alternador solo se muestra si el texto realmente desborda esas 3 líneas
 * (se mide con `ResizeObserver`, nunca durante el render). Una vez detectado
 * el desbordamiento se mantiene el alternador visible aunque, ya expandido,
 * el bloque deje de desbordar — evita que el botón desaparezca al expandir.
 *
 * Analítica: cada alternancia emite un `ui_interaction` de tipo `accordion`
 * (`component_action: expand | collapse`), el patrón que fija
 * `components-fingerprint/Entradas + FAQs.csv` para un bloque desplegable.
 * Fuera de `<PageAnalyticsProvider>` (páginas aún sin instrumentar, tests) no
 * emite nada y no falla: `useOptionalPageAnalytics` devuelve `null`.
 */
export function ExpandableDescription({ text, html, analyticsId }: ExpandableDescriptionProps) {
  const [expanded, setExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const analytics = useOptionalPageAnalytics()

  const measureRef = useCallback((node: HTMLSpanElement | null) => {
    if (!node) return undefined

    const check = () => {
      if (node.scrollHeight > node.clientHeight + 1) setHasOverflow(true)
    }
    check()

    const observer = new ResizeObserver(check)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  function handleToggle() {
    const nextExpanded = !expanded
    analytics?.trackUiInteraction({
      component_type: 'accordion',
      component_action: nextExpanded ? 'expand' : 'collapse',
      component_id: analyticsId ?? `${analytics.context.page_type}_description`,
      component_label: ANALYTICS_LABEL,
      selected_value: NO_APLICA,
      module_name: NOT_SET,
      module_type: NO_APLICA,
      module_position: NO_APLICA,
    })
    setExpanded(nextExpanded)
  }

  return (
    <span className={styles.root}>
      {html !== undefined ? (
        <span
          ref={measureRef}
          className={expanded ? styles.expanded : styles.clamped}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <span ref={measureRef} className={expanded ? styles.expanded : styles.clamped}>
          {text}
        </span>
      )}
      {hasOverflow && (
        <button type="button" className={styles.toggle} onClick={handleToggle}>
          {expanded ? 'Ver menos' : 'Ver más'}
        </button>
      )}
    </span>
  )
}
